import {
  Text as RNText,
  type TextProps as RNTextProps,
  StyleSheet,
} from "react-native";

import { withFallback } from "@/theme/fonts";
import type { AppTheme } from "@/theme/theme";
import { lineHeightFor } from "@/theme/tokens";
import { useTheme } from "@/theme/useTheme";

type Variant = "heading" | "body";
type Weight = "regular" | "medium" | "semiBold" | "bold";
type Tone = "default" | "muted" | "primary";
type Size = keyof AppTheme["fontSize"];

export type TextProps = RNTextProps & {
  /** heading = Poppins, body = DM Sans (FR-021). */
  variant?: Variant;
  weight?: Weight;
  size?: Size;
  tone?: Tone;
};

function family(
  theme: AppTheme,
  variant: Variant,
  weight: Weight | undefined,
): string {
  if (variant === "heading") {
    // Poppins ships 500/600/700 only; "regular" maps to medium.
    const w =
      weight === "bold"
        ? "bold"
        : weight === "medium" || weight === "regular"
          ? "medium"
          : "semiBold";
    return theme.font.heading[w];
  }
  return theme.font.body[weight ?? "regular"];
}

/** Text with the token type scale, fonts and colours (FR-020, FR-021). */
export function Text({
  variant = "body",
  weight,
  size,
  tone = "default",
  style,
  ...rest
}: TextProps) {
  const theme = useTheme();
  const fontSize =
    theme.fontSize[size ?? (variant === "heading" ? "x2l" : "base")];
  const color =
    tone === "muted"
      ? theme.color.textMuted
      : tone === "primary"
        ? theme.color.primaryText
        : theme.color.text;
  return (
    <RNText
      {...rest}
      style={StyleSheet.flatten([
        {
          fontFamily: withFallback(family(theme, variant, weight)),
          fontSize,
          lineHeight: lineHeightFor(fontSize),
          color,
        },
        style,
      ])}
    />
  );
}
